import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Node, Edge, Workflow, NodeTypeDef, OperatorConfig } from '../types';
import { workflowApi } from '../services/api';

// 节点类型到中文标签的映射
const NODE_TYPE_LABELS: Record<string, string> = {
  load_excel: '加载 Excel',
  load_csv: '加载 CSV',
  load_api: 'API 数据',
  filter: '筛选',
  sort: '排序',
  compute: '计算列',
  dedup: '去重',
  aggregate: '聚合统计',
  group_by: '分组',
  join: '关联合并',
  append: '追加',
  diff: '差异对比',
  export_excel: '导出 Excel',
  export_csv: '导出 CSV',
};

// 节点数据（ReactFlow 格式）
export interface FlowNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: {
    label: string;
    config: OperatorConfig;
    status?: 'idle' | 'running' | 'success' | 'error';
    rowCount?: number;
  };
}

export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  type?: string;
}

interface WorkflowState {
  // 当前工作流
  currentWorkflow: Workflow | null;
  
  // 节点和边
  nodes: FlowNode[];
  edges: FlowEdge[];
  
  // 选中的节点
  selectedNode: FlowNode | null;
  
  // 节点类型定义
  nodeTypes: NodeTypeDef[];
  
  // 操作
  setCurrentWorkflow: (workflow: Workflow | null) => void;
  addNode: (node: FlowNode) => void;
  updateNode: (id: string, updates: Partial<FlowNode>) => void;
  removeNode: (id: string) => void;
  addEdge: (edge: FlowEdge) => void;
  removeEdge: (id: string) => void;
  setSelectedNode: (node: FlowNode | null) => void;
  updateNodeConfig: (id: string, config: OperatorConfig) => void;
  setNodeStatus: (id: string, status: FlowNode['data']['status']) => void;
  loadWorkflow: (workflow: Workflow) => void;
  clearWorkflow: () => void;
  fetchNodeTypes: () => Promise<void>;
  
  // 转换为 DAG 格式
  toDAG: () => { nodes: Node[]; edges: Edge[] };
}

export const useWorkflowStore = create<WorkflowState>()(
  persist(
    (set, get) => ({
      currentWorkflow: null,
      nodes: [],
      edges: [],
      selectedNode: null,
      nodeTypes: [],

      setCurrentWorkflow: (workflow) => set({ currentWorkflow: workflow }),

      addNode: (node) => set((state) => ({ nodes: [...state.nodes, node] })),

      updateNode: (id, updates) =>
        set((state) => ({
          nodes: state.nodes.map((n) =>
            n.id === id ? { ...n, ...updates } : n
          ),
        })),

      removeNode: (id) =>
        set((state) => {
          const incomingEdges = state.edges.filter((e) => e.target === id);
          const outgoingEdges = state.edges.filter((e) => e.source === id);
          const remainingEdges = state.edges.filter((e) => e.source !== id && e.target !== id);

          // Reconnect: for each upstream → deleted → downstream pair, wire upstream directly to downstream
          const reconnectEdges: FlowEdge[] = [];
          incomingEdges.forEach((inEdge) => {
            outgoingEdges.forEach((outEdge) => {
              const alreadyExists = remainingEdges.some(
                (e) => e.source === inEdge.source && e.target === outEdge.target
              );
              if (!alreadyExists) {
                reconnectEdges.push({
                  id: `e_${inEdge.source}_${outEdge.target}`,
                  source: inEdge.source,
                  target: outEdge.target,
                });
              }
            });
          });

          return {
            nodes: state.nodes.filter((n) => n.id !== id),
            edges: [...remainingEdges, ...reconnectEdges],
            selectedNode: state.selectedNode?.id === id ? null : state.selectedNode,
          };
        }),

      addEdge: (edge) => set((state) => ({ edges: [...state.edges, edge] })),

      removeEdge: (id) =>
        set((state) => ({
          edges: state.edges.filter((e) => e.id !== id),
        })),

      setSelectedNode: (node) => set({ selectedNode: node }),

      updateNodeConfig: (id, config) =>
        set((state) => ({
          nodes: state.nodes.map((n) =>
            n.id === id ? { ...n, data: { ...n.data, config } } : n
          ),
        })),

      setNodeStatus: (id, status) =>
        set((state) => ({
          nodes: state.nodes.map((n) =>
            n.id === id ? { ...n, data: { ...n.data, status } } : n
          ),
        })),

      loadWorkflow: (workflow) => {
        // 将 Workflow DAG 转换为 FlowNode 和 FlowEdge
        const nodes: FlowNode[] = [];
        const edges: FlowEdge[] = [];

        // 转换节点
        Object.values(workflow.dag.nodes).forEach((node) => {
          nodes.push({
            id: node.id,
            type: node.type,
            position: node.position || { x: 0, y: 0 },
            data: {
              label: NODE_TYPE_LABELS[node.type] || node.type,
              config: node.config,
              status: 'idle',
            },
          });
        });

        // 转换边
        workflow.dag.edges.forEach((edge, index) => {
          edges.push({
            id: `e${index}`,
            source: edge.from,
            target: edge.to,
          });
        });

        set({
          currentWorkflow: workflow,
          nodes,
          edges,
          selectedNode: null,
        });
      },

      clearWorkflow: () =>
        set({
          currentWorkflow: null,
          nodes: [],
          edges: [],
          selectedNode: null,
        }),

      fetchNodeTypes: async () => {
        try {
          const response = await workflowApi.getNodeTypes();
          if (response.code === 0) {
            set({ nodeTypes: response.data });
          }
        } catch (error) {
          console.error('Failed to fetch node types:', error);
        }
      },

      toDAG: () => {
        const state = get();
        const nodes: Node[] = state.nodes.map((n) => ({
          id: n.id,
          type: n.type,
          inputs: state.edges
            .filter((e) => e.target === n.id)
            .map((e) => e.source),
          config: n.data.config,
          position: n.position,
        }));

        const edges: Edge[] = state.edges.map((e) => ({
          from: e.source,
          to: e.target,
        }));

        return { nodes, edges };
      },
    }),
    {
      name: 'workflow-storage',
      partialize: (state) => ({
        currentWorkflow: state.currentWorkflow,
        nodeTypes: state.nodeTypes,
      }),
    }
  )
);
